import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

/**
 * List of curated countries to show by default (high priority)
 * These countries appear first in the dropdown before "Other countries..."
 */
const CURATED_COUNTRIES = [
  'US', // United States
  'IN', // India
  'GB', // United Kingdom
  'AU', // Australia
  'PH', // Philippines
  'JP', // Japan
  'CN', // China
  'DE', // Germany
  'FR'  // France
];

/**
 * Converts ISO 3166-1 alpha-2 country code to flag emoji
 * Uses Unicode regional indicator symbols
 *
 * @param {string} countryCode - Two-letter country code (e.g., 'US')
 * @returns {string} Flag emoji (e.g., '🇺🇸')
 * @example
 * getCountryFlag('US') // Returns '🇺🇸'
 * getCountryFlag('IN') // Returns '🇮🇳'
 */
export const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) {
    return '🏳️'; // Default flag for invalid codes
  }

  try {
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch (error) {
    console.error('Error generating flag emoji:', error);
    return '🏳️';
  }
};

/**
 * Gets the localized country name from ISO country code
 * Uses Intl.DisplayNames API with fallback for unsupported browsers
 *
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US')
 * @returns {string} Localized country name (e.g., 'United States')
 */
export const getCountryName = (countryCode) => {
  if (!countryCode) return countryCode;

  try {
    // Use Intl.DisplayNames for localized country names
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(countryCode.toUpperCase()) || countryCode;
  } catch (error) {
    // Fallback for browsers that don't support Intl.DisplayNames
    console.warn('Intl.DisplayNames not supported, using country code:', error);
    return countryCode;
  }
};

/**
 * Country data cache to avoid regenerating on every call
 */
let cachedCountries = null;

/**
 * Extracts all country data from libphonenumber-js
 * Returns array of country objects with calling codes, names, and flags
 * Results are cached for performance
 *
 * @returns {Array<Object>} Array of country objects
 * @example
 * [
 *   {
 *     code: '+1',              // Calling code for display
 *     countryCode: 'US',       // ISO 3166-1 alpha-2 code
 *     label: 'United States',  // Localized country name
 *     flag: '🇺🇸',             // Unicode flag emoji
 *     priority: 1              // 1 = curated, 2 = other
 *   },
 *   ...
 * ]
 */
export const getAllCountries = () => {
  // Return cached data if available
  if (cachedCountries) {
    return cachedCountries;
  }

  try {
    // Get all country codes from libphonenumber-js
    const countryCodes = getCountries();

    // Transform into our country data structure
    const countries = countryCodes.map(countryCode => {
      const callingCode = getCountryCallingCode(countryCode);
      const countryName = getCountryName(countryCode);
      const flag = getCountryFlag(countryCode);
      const isCurated = CURATED_COUNTRIES.includes(countryCode);

      return {
        code: `+${callingCode}`,
        countryCode: countryCode,
        label: countryName,
        flag: flag,
        priority: isCurated ? 1 : 2
      };
    });

    // Sort: curated countries first (priority 1), then alphabetically by label
    countries.sort((a, b) => {
      // First sort by priority
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      // Then sort alphabetically by label
      return a.label.localeCompare(b.label);
    });

    // Cache the result
    cachedCountries = countries;
    return countries;

  } catch (error) {
    console.error('Error loading countries from libphonenumber-js:', error);

    // Fallback to hardcoded list if library fails
    return getFallbackCountries();
  }
};

/**
 * Fallback country list in case libphonenumber-js fails to load
 * Uses the same structure as getAllCountries()
 *
 * @returns {Array<Object>} Hardcoded fallback country list
 */
const getFallbackCountries = () => {
  return [
    { code: '+1', countryCode: 'US', label: 'United States', flag: '🇺🇸', priority: 1 },
    { code: '+91', countryCode: 'IN', label: 'India', flag: '🇮🇳', priority: 1 },
    { code: '+44', countryCode: 'GB', label: 'United Kingdom', flag: '🇬🇧', priority: 1 },
    { code: '+61', countryCode: 'AU', label: 'Australia', flag: '🇦🇺', priority: 1 },
    { code: '+63', countryCode: 'PH', label: 'Philippines', flag: '🇵🇭', priority: 1 },
    { code: '+81', countryCode: 'JP', label: 'Japan', flag: '🇯🇵', priority: 1 },
    { code: '+86', countryCode: 'CN', label: 'China', flag: '🇨🇳', priority: 1 },
    { code: '+49', countryCode: 'DE', label: 'Germany', flag: '🇩🇪', priority: 1 },
    { code: '+33', countryCode: 'FR', label: 'France', flag: '🇫🇷', priority: 1 }
  ];
};

/**
 * Gets only the curated countries (for initial display)
 *
 * @returns {Array<Object>} Array of curated country objects
 */
export const getCuratedCountries = () => {
  const allCountries = getAllCountries();
  return allCountries.filter(country => country.priority === 1);
};

/**
 * Clears the country cache (useful for testing or if data needs to be refreshed)
 */
export const clearCountryCache = () => {
  cachedCountries = null;
};
